import React, { useState, useEffect, useCallback, useId, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
const { Panel: DialogPanel, Title: DialogTitle } = Dialog;
const { Child: TransitionChild } = Transition;

import { XMarkIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { clsx } from 'clsx';
import {
  counterpartyOptionLabel,
  useCreateCounterparty,
  useCounterpartyDetail,
  useCounterpartySearch,
  useUpdateCounterparty,
} from '@/hooks/counterparty';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { getSupportedCurrencies } from '@/services/currency/currency-service';
import { agreementPayloadFields } from '@/services/counterparty/counterparty-service';
import {
  AgreementFormEntry,
  CounterpartyListRow,
  CounterpartyOption,
  RepoMarginMethodology,
} from '@/types/counterparty.types';
import type { FrameworkAgreement } from '@/types/trades.types';
import { useTourStateStore } from '@/stores';
import { getErrorMessage } from '@/utils/get-error-message';

interface AddCounterpartyAgreementModalProps {
  isOpen: boolean;
  onClose: () => void;
  // LR-440: when `mode === 'edit'`, the form is prefilled from `counterparty`
  // and submits a PATCH instead of a POST. Defaults to create.
  mode?: 'create' | 'edit';
  counterparty?: CounterpartyListRow;
}

// The stored value goes in as an extra: a currency outside the shared list would otherwise
// render as the select's first option and be saved over the real one.
const currencyOptions = (current: string | undefined) =>
  getSupportedCurrencies(current ? [current] : []).map((c) => ({ v: c, l: c }));

// LR-579: the repo margin call methodology election (Method A / Method B).
const REPO_MARGIN_METHODOLOGY_OPTIONS = [
  { v: 'with_accrued', l: 'Method B — include accrued interest' },
  { v: 'without_accrued', l: 'Method A — exclude accrued interest' },
];

const AGREEMENT_TYPES: { type: FrameworkAgreement; label: string }[] = [
  { type: 'gmsla', label: 'GMSLA' },
  { type: 'gmra', label: 'GMRA' },
];

const blankAgreement = (type: FrameworkAgreement): AgreementFormEntry => ({
  type,
  enabled: type === 'gmsla',
  currency: 'USD',
  validFrom: '',
  validTo: '',
  isPerpetual: false,
});

const INITIAL_FORM_DATA = {
  name: '',
  clientId: '',
  country: '',
  agreements: AGREEMENT_TYPES.map(({ type }) => blankAgreement(type)),
  parentId: null as number | null,
  parentName: null as string | null,
  creditRating: '',
  notes: '',
  // LR-389: minimum transfer amount (MTA) gating the MTM margin call.
  minimumTransferAmount: '',
  minimumTransferAmountCurrency: 'USD',
  // LR-579: repo margin call methodology; Method B is the usual market practice.
  repoMarginMethodology: 'with_accrued' as RepoMarginMethodology,
  // LR-440: counterparty active flag (editable in edit mode).
  isActive: true,
  // Hidden fields
  legalEntity: '',
  agreementId: '',
  status: 'Active',
  settlement: 'T+1',
  recallNotice: 'T+1',
  delivery: 'DVP',
  margin: 'Both',
  venue: 'Both',
  dividend: 'Pass-through',
  collateralType: 'Both',
  terminationNotice: 'T+30',
  governingLaw: 'English Law',
};

// LR-440: prefill the form from an existing counterparty row for edit mode.
const buildEditFormData = (cp: CounterpartyListRow): typeof INITIAL_FORM_DATA => ({
  ...INITIAL_FORM_DATA,
  name: cp.name ?? '',
  clientId: cp.clientId ?? '',
  // Prefill the real country only. `legalEntity` is a display-only alias
  // (`country || 'International'`) and is NOT in the update serializer — using it
  // as a fallback would PATCH the literal 'International' placeholder back onto a
  // blank-country counterparty (LR-440).
  country: cp.country || '',
  agreements: AGREEMENT_TYPES.map(({ type }) => {
    const held = cp.agreements.find((agreement) => agreement.type === type);
    if (!held) return { ...blankAgreement(type), enabled: false };
    const loaded = {
      currency: held.currency,
      validFrom: held.validFrom ?? '',
      validTo: held.validTo ?? '',
      isPerpetual: held.isPerpetual,
    };
    return { type, enabled: true, apiId: held.apiId, ...loaded, stored: agreementPayloadFields(loaded) };
  }),
  parentId: cp.parentId ?? null,
  parentName: cp.parentName ?? null,
  creditRating: cp.creditRating ?? '',
  notes: cp.notes ?? '',
  isActive: cp.isActive,
  minimumTransferAmount: cp.minimumTransferAmount != null ? String(cp.minimumTransferAmount) : '',
  minimumTransferAmountCurrency: cp.minimumTransferAmountCurrency || 'USD',
  repoMarginMethodology: cp.repoMarginMethodology ?? INITIAL_FORM_DATA.repoMarginMethodology,
});

export const AddCounterpartyAgreementModal: React.FC<AddCounterpartyAgreementModalProps> = ({ isOpen, onClose, mode = 'create', counterparty }) => {
  const isEditMode = mode === 'edit';
  const createCounterparty = useCreateCounterparty();
  const updateCounterparty = useUpdateCounterparty();
  // LR-440: the mutation backing the current mode — drives the submit button's
  // loading state and the inline save-error banner (shared by create and edit).
  const activeMutation = isEditMode ? updateCounterparty : createCounterparty;
  // LR-440: the list endpoint (which feeds this modal's `counterparty` row) omits
  // `notes`, so the edit prefill starts blank for it. Load the full counterparty in
  // edit mode and fold the real notes in (see the effect below). Disabled for create.
  const { data: editDetail } = useCounterpartyDetail(isEditMode && counterparty ? counterparty.id : 0);
  // While the tour is active, treat clicks outside the panel as no-ops —
  // Headless-UI's onClose otherwise fires when the user clicks the tour card
  // (portal-rendered to body, outside DialogPanel) and closes the modal mid-
  // tour. Reading from the dedicated store (not `useTour()`) avoids requiring
  // a TourProvider in tests / Storybook for an unrelated modal. The X button
  // still calls handleClose directly, and the tour's own onLeave clicks the X
  // explicitly to close on step transitions.
  const isTourActive = useTourStateStore((s) => s.isActive);
  const [formData, setFormData] = useState(
    isEditMode && counterparty ? buildEditFormData(counterparty) : INITIAL_FORM_DATA
  );
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  const [sectionStates, setSectionStates] = useState({
    counterpartyInfo: true,
    agreementDetails: true,
    dropdownTerms: true,
  });

  // Handle closing
  const handleCloseWithoutConfirmation = useCallback(() => {
    onClose();
    setFormData(INITIAL_FORM_DATA);
    setHasFormChanges(false);
    setShowCloseConfirmation(false);
  }, [onClose]);

  const handleClose = useCallback(() => {
    if (hasFormChanges) {
      setShowCloseConfirmation(true);
    } else {
      handleCloseWithoutConfirmation();
    }
  }, [hasFormChanges, handleCloseWithoutConfirmation]);

  // Handle escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !showCloseConfirmation) {
        event.preventDefault();
        event.stopPropagation();
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey, true);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey, true);
    };
  }, [isOpen, handleClose, showCloseConfirmation]);

  // LR-440: once the full counterparty has loaded in edit mode, fold its `notes`
  // into the form. Apply it only while the field is still blank (so it never
  // clobbers a value the user already typed) and only once (so a background
  // refetch can't overwrite edits). Does not flip `hasFormChanges`.
  const notesPrefilledRef = useRef(false);
  // Reset the one-shot guard on the counterparty being edited (the selection id),
  // not on the recomputed `editDetail` reference — so switching to another
  // counterparty re-folds its own notes. The modal is mounted per-edit today, but
  // keying the reset on the id keeps the prefill correct if that ever changes.
  useEffect(() => {
    notesPrefilledRef.current = false;
  }, [counterparty?.id]);
  useEffect(() => {
    if (isEditMode && editDetail && !notesPrefilledRef.current) {
      notesPrefilledRef.current = true;
      setFormData(prev => (prev.notes === '' ? { ...prev, notes: editDetail.notes ?? '' } : prev));
    }
  }, [isEditMode, editDetail]);

  const toggleSection = (section: keyof typeof sectionStates) => {
    setSectionStates((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleFormDataChange = (updates: Partial<typeof INITIAL_FORM_DATA>) => {
    setFormData(prev => ({ ...prev, ...updates }));
    setHasFormChanges(true);
  };

  // Perpetual clears the expiry only. The start (validFrom) is kept: a perpetual agreement
  // still has one, and wiping it would null agreement_date on save (LR-440).
  const handleAgreementChange = (type: FrameworkAgreement, updates: Partial<AgreementFormEntry>) => {
    setFormData((prev) => ({
      ...prev,
      agreements: prev.agreements.map((entry) => {
        if (entry.type !== type) return entry;
        const next = { ...entry, ...updates };
        return next.isPerpetual ? { ...next, validTo: '' } : next;
      }),
    }));
    setHasFormChanges(true);
  };
  const hasAnAgreement = formData.agreements.some((entry) => entry.enabled);

  const [parentQuery, setParentQuery] = useState('');
  // Only a head of a group (or a record standing alone) can head this one, and only a live one;
  // the server refuses the rest.
  const ownId = counterparty?.id;
  const canHeadThisGroup = useCallback(
    (option: CounterpartyOption) => option.parentId == null && option.id !== ownId,
    [ownId],
  );
  // A record with members of its own cannot join another group at all.
  const headsAGroup = (editDetail?.groupMemberCount ?? 0) > 0;
  const parentLabelId = useId();
  const parentButtonId = useId();
  const {
    options: parentOptions,
    isLoading: parentsLoading,
    isError: parentsError,
    hasNextPage: parentsHaveMore,
    fetchNextPage: fetchMoreParents,
    isFetchingNextPage: parentsLoadingMore,
  } = useCounterpartySearch(parentQuery, true, { eligible: canHeadThisGroup });
  const NO_PARENT = '';
  const parentPickerOptions = [
    { value: NO_PARENT, label: 'None — a legal entity of its own' },
    ...parentOptions,
  ];
  const pickParent = (value: string) => {
    if (value === NO_PARENT) {
      handleFormDataChange({ parentId: null, parentName: null });
      return;
    }
    const option = parentOptions.find((o) => o.value === value);
    handleFormDataChange({ parentId: Number(value), parentName: option?.label ?? null });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const onSuccess = () => {
      setHasFormChanges(false);
      handleCloseWithoutConfirmation();
    };
    if (isEditMode && counterparty) {
      // LR-440: until the full counterparty has loaded, we have no authoritative
      // `notes` value (the list row omits it). If the user hasn't typed any notes
      // either, omit the field so the PATCH leaves the stored notes untouched
      // instead of blanking them.
      const data: Record<string, unknown> = { ...formData };
      if (!editDetail && formData.notes === '') {
        delete data.notes;
      }
      updateCounterparty.mutate({ id: counterparty.id, data }, { onSuccess });
    } else {
      createCounterparty.mutate(formData, { onSuccess });
    }
  };

  const labelClassName = 'block text-xs text-gray-500 mb-1';
  const inputClassName = 'w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500';

  const SectionHeader: React.FC<{ title: string; section: keyof typeof sectionStates }> = ({ title, section }) => (
    <button
      type="button"
      onClick={() => toggleSection(section)}
      className="flex items-center justify-between w-full py-3 px-4 bg-gray-50 hover:bg-gray-100 border-b border-gray-200 transition-colors"
    >
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {sectionStates[section] ? (
        <ChevronUpIcon className="h-5 w-5 text-gray-500" />
      ) : (
        <ChevronDownIcon className="h-5 w-5 text-gray-500" />
      )}
    </button>
  );

  return (
    <>
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={isTourActive ? () => {} : handleClose} static={isTourActive || undefined}>
        <TransitionChild
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-[101] overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 py-10 text-center sm:items-start sm:p-0">
            <TransitionChild
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel data-tour="counterparty-form-fields" className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl max-h-[calc(100vh-80px)] flex flex-col">
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <DialogTitle as="h3" className="text-lg font-semibold text-gray-900">
                    {isEditMode ? 'Edit Counterparty' : 'New Counterparty Agreement'}
                  </DialogTitle>
                  <button data-tour="counterparty-modal-close" onClick={handleClose} className="text-gray-400 hover:text-gray-500 focus:outline-none">
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
                  <div className="divide-y divide-gray-200">
                    {/* Section 1: Counterparty Info */}
                    <div>
                      <SectionHeader title="Counterparty Information" section="counterpartyInfo" />
                      {sectionStates.counterpartyInfo && (
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <InputField 
                            label="Counterparty Name" 
                            required 
                            value={formData.name} 
                            onChange={(v: string) => handleFormDataChange({name: v})} 
                            labelClassName={labelClassName} 
                            inputClassName={inputClassName} 
                            placeholder="e.g. Goldman Sachs International"
                          />
                          <InputField
                            label="Counterparty ID"
                            required
                            value={formData.clientId}
                            disabled={isEditMode}
                            onChange={(v: string) => handleFormDataChange({clientId: v})}
                            labelClassName={labelClassName}
                            inputClassName={inputClassName}
                            placeholder="e.g. GS001"
                          />
                          <InputField 
                            label="Country" 
                            value={formData.country} 
                            onChange={(v: string) => handleFormDataChange({country: v})} 
                            labelClassName={labelClassName} 
                            inputClassName={inputClassName} 
                            placeholder="e.g. United Kingdom"
                          />
                          <InputField
                            label="Credit Rating"
                            value={formData.creditRating}
                            onChange={(v: string) => handleFormDataChange({creditRating: v})}
                            labelClassName={labelClassName}
                            inputClassName={inputClassName}
                            placeholder="e.g. AAA"
                          />
                          {/* Another record of the same legal entity: kept apart because it is a
                              separate Backoffice client, shown together with it. */}
                          <div className="md:col-span-2">
                            <span id={parentLabelId} className={labelClassName}>Same Legal Entity As</span>
                            <SearchableSelect
                              disabled={headsAGroup}
                              value={formData.parentId != null ? String(formData.parentId) : NO_PARENT}
                              onChange={pickParent}
                              options={parentPickerOptions}
                              selectedLabel={
                                formData.parentId != null && formData.parentName
                                  ? counterpartyOptionLabel(formData.parentName, true)
                                  : undefined
                              }
                              placeholder="None — a legal entity of its own"
                              searchPlaceholder="Search counterparties…"
                              className="w-full"
                              onSearchChange={setParentQuery}
                              isLoading={parentsLoading}
                              isError={parentsError}
                              hasMore={parentsHaveMore}
                              onLoadMore={() => fetchMoreParents({ cancelRefetch: false })}
                              isLoadingMore={parentsLoadingMore}
                              buttonProps={{
                                id: parentButtonId,
                                // The visible label plus the button's own text, so the pick is announced too.
                                'aria-labelledby': `${parentLabelId} ${parentButtonId}`,
                              }}
                            />
                            {headsAGroup && (
                              <p className="mt-1 text-xs text-gray-500">
                                Other records are grouped under this one, so it cannot join another group.
                              </p>
                            )}
                          </div>
                          {/* LR-389: Minimum Transfer Amount (MTA) — value + currency. */}
                          <InputField
                            label="Min Transfer Amount"
                            type="number"
                            value={formData.minimumTransferAmount}
                            onChange={(v: string) => handleFormDataChange({minimumTransferAmount: v})}
                            labelClassName={labelClassName}
                            inputClassName={inputClassName}
                            placeholder="e.g. 50000"
                          />
                          <SelectField
                            label="MTA Currency"
                            value={formData.minimumTransferAmountCurrency}
                            options={currencyOptions(formData.minimumTransferAmountCurrency)}
                            onChange={(v: string) => handleFormDataChange({minimumTransferAmountCurrency: v})}
                            labelClassName={labelClassName}
                            inputClassName={inputClassName}
                          />
                          {/* LR-579: repo margin call methodology — the GMRA "price plus accrued"
                              election, sitting with the other margining terms. */}
                          <div className="md:col-span-2">
                            <SelectField
                              label="Repo Margin Call Methodology"
                              value={formData.repoMarginMethodology}
                              options={REPO_MARGIN_METHODOLOGY_OPTIONS}
                              onChange={(v: string) =>
                                handleFormDataChange({ repoMarginMethodology: v as RepoMarginMethodology })}
                              labelClassName={labelClassName}
                              inputClassName={inputClassName}
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              Method B values each repo at its Repurchase Price — booked cash plus repo
                              interest accrued and not yet paid. Method A margins the price move only.
                            </p>
                          </div>
                          {/* LR-440: Active flag — counterparty-level, editable only in edit mode. */}
                          {isEditMode && (
                            <div className="md:col-span-2 pt-1">
                              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={formData.isActive}
                                  onChange={(e) => handleFormDataChange({isActive: e.target.checked})}
                                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm font-medium text-gray-700">Active</span>
                              </label>
                            </div>
                          )}
                          <div className="md:col-span-2">
                            <label className={labelClassName}>Notes</label>
                            <textarea
                              value={formData.notes}
                              onChange={(e) => handleFormDataChange({notes: e.target.value})}
                              className={clsx(inputClassName, "h-20 resize-none")}
                              placeholder="Additional notes..."
                            />
                          </div>
                          {/* Hidden: Legal Entity */}
                          {false && (
                            <div className="md:col-span-2">
                              <InputField 
                                label="Legal Entity" 
                                value={formData.legalEntity} 
                                onChange={(v: string) => handleFormDataChange({legalEntity: v})} 
                                labelClassName={labelClassName} 
                                inputClassName={inputClassName} 
                                placeholder="e.g. United Kingdom"
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Section 2: Agreements — at most one per type, each in its own currency */}
                    <div>
                      <SectionHeader title="Agreements" section="agreementDetails" />
                      {sectionStates.agreementDetails && (
                        <div className="p-6 space-y-4">
                          {formData.agreements.map((entry) => {
                            const label = AGREEMENT_TYPES.find((t) => t.type === entry.type)?.label;
                            // An agreement that exists cannot be switched off: pools and trades hang off it.
                            const isStored = entry.apiId != null;
                            return (
                              <fieldset
                                key={entry.type}
                                data-testid={`agreement-${entry.type}`}
                                className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end"
                              >
                                {/* Both rows carry the same field labels; the legend tells them apart. */}
                                <legend className="sr-only">{label} agreement</legend>
                                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer pb-1">
                                  <input
                                    type="checkbox"
                                    checked={entry.enabled}
                                    disabled={isStored}
                                    aria-label={`${label} agreement`}
                                    onChange={(e) => handleAgreementChange(entry.type, { enabled: e.target.checked })}
                                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span className="text-sm font-medium text-gray-900">{label}</span>
                                </label>
                                {entry.enabled && (
                                  <>
                                    <SelectField
                                      label={`${label} Currency`}
                                      required
                                      value={entry.currency}
                                      options={currencyOptions(entry.currency)}
                                      onChange={(v: string) => handleAgreementChange(entry.type, { currency: v })}
                                      labelClassName={labelClassName}
                                      inputClassName={inputClassName}
                                    />
                                    <InputField
                                      label="Valid From"
                                      type="date"
                                      value={entry.validFrom}
                                      onChange={(v: string) => handleAgreementChange(entry.type, { validFrom: v })}
                                      labelClassName={labelClassName}
                                      inputClassName={inputClassName}
                                    />
                                    <InputField
                                      label="Valid To"
                                      type="date"
                                      value={entry.validTo}
                                      disabled={entry.isPerpetual}
                                      onChange={(v: string) => handleAgreementChange(entry.type, { validTo: v })}
                                      labelClassName={labelClassName}
                                      inputClassName={inputClassName}
                                    />
                                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer pb-1">
                                      <input
                                        type="checkbox"
                                        checked={entry.isPerpetual}
                                        onChange={(e) => handleAgreementChange(entry.type, { isPerpetual: e.target.checked })}
                                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                      />
                                      <span className="text-sm font-medium text-gray-700">Perpetual</span>
                                    </label>
                                  </>
                                )}
                              </fieldset>
                            );
                          })}
                          <p className="text-xs text-gray-500">
                            Cash pools under an agreement are opened in its currency. The currency
                            cannot change while a cash pool in it holds collateral.
                          </p>
                          {!hasAnAgreement && (
                            <p role="alert" className="text-xs text-red-600">
                              A counterparty needs at least one agreement.
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Section 3: Standard Terms - HIDDEN as not in API */}
                    {false && (
                      <div>
                        <SectionHeader title="Terms" section="dropdownTerms" />
                        {sectionStates.dropdownTerms && (
                          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <SelectField 
                              label="Settlement" 
                              value={formData.settlement} 
                              options={[{v: 'T+0', l: 'T+0'}, {v: 'T+1', l: 'T+1'}, {v: 'T+2', l: 'T+2'}, {v: 'T+3', l: 'T+3'}]} 
                              onChange={(v: string) => handleFormDataChange({settlement: v})} 
                              labelClassName={labelClassName} 
                              inputClassName={inputClassName} 
                            />
                            <SelectField 
                              label="Recall Notice" 
                              value={formData.recallNotice} 
                              options={[{v: 'T+0', l: 'T+0'}, {v: 'T+1', l: 'T+1'}, {v: 'T+2', l: 'T+2'}, {v: 'T+3', l: 'T+3'}]} 
                              onChange={(v: string) => handleFormDataChange({recallNotice: v})} 
                              labelClassName={labelClassName} 
                              inputClassName={inputClassName} 
                            />
                            <SelectField 
                              label="Delivery" 
                              value={formData.delivery} 
                              options={[{v: 'DVP', l: 'DVP'}, {v: 'FOP', l: 'FOP'}, {v: 'PREPAYMENT', l: 'PREPAYMENT'}, {v: 'PREDELIVERY', l: 'PREDELIVERY'}]} 
                              onChange={(v: string) => handleFormDataChange({delivery: v})} 
                              labelClassName={labelClassName} 
                              inputClassName={inputClassName} 
                            />
                            <SelectField 
                              label="Margin" 
                              value={formData.margin} 
                              options={[{v: 'Cash', l: 'Cash'}, {v: 'Securities', l: 'Securities'}, {v: 'Both', l: 'Both'}]} 
                              onChange={(v: string) => handleFormDataChange({margin: v})} 
                              labelClassName={labelClassName} 
                              inputClassName={inputClassName} 
                            />
                            <SelectField 
                              label="Venue" 
                              value={formData.venue} 
                              options={[
                                {v: 'OTC', l: 'OTC'}, {v: 'Exchange', l: 'Exchange'}, {v: 'Both', l: 'Both'},
                                {v: 'DTC', l: 'DTC'}, {v: 'EUROCLEAR', l: 'EUROCLEAR'}, {v: 'CLEARSTREAM', l: 'CLEARSTREAM'},
                                {v: 'CREST', l: 'CREST'}, {v: 'HKSCC', l: 'HKSCC'}, {v: 'JASDEC', l: 'JASDEC'}
                              ]} 
                              onChange={(v: string) => handleFormDataChange({venue: v})} 
                              labelClassName={labelClassName} 
                              inputClassName={inputClassName} 
                            />
                            <SelectField 
                              label="Dividend" 
                              value={formData.dividend} 
                              options={[{v: 'Manufactured', l: 'Manufactured'}, {v: 'Pass-through', l: 'Pass-through'}, {v: 'Split', l: 'Split'}]} 
                              onChange={(v: string) => handleFormDataChange({dividend: v})} 
                              labelClassName={labelClassName} 
                              inputClassName={inputClassName} 
                            />
                            <SelectField 
                              label="Collateral Type" 
                              value={formData.collateralType} 
                              options={[{v: 'Cash', l: 'Cash'}, {v: 'Securities', l: 'Securities'}, {v: 'Mixed', l: 'Mixed'}, {v: 'Both', l: 'Both'}]} 
                              onChange={(v: string) => handleFormDataChange({collateralType: v})} 
                              labelClassName={labelClassName} 
                              inputClassName={inputClassName} 
                            />
                            <SelectField 
                              label="Termination Notice" 
                              value={formData.terminationNotice} 
                              options={[
                                {v: 'T+0', l: 'T+0'}, {v: 'T+1', l: 'T+1'}, {v: 'T+2', l: 'T+2'}, {v: 'T+3', l: 'T+3'},
                                {v: 'T+5', l: 'T+5'}, {v: 'T+7', l: 'T+7'}, {v: 'T+10', l: 'T+10'}, {v: 'T+14', l: 'T+14'}, {v: 'T+30', l: 'T+30'}
                              ]} 
                              onChange={(v: string) => handleFormDataChange({terminationNotice: v})} 
                              labelClassName={labelClassName} 
                              inputClassName={inputClassName} 
                            />
                            <SelectField 
                              label="Governing Law" 
                              value={formData.governingLaw} 
                              options={[
                                {v: 'English Law', l: 'English Law'}, {v: 'New York Law', l: 'New York Law'},
                                {v: 'Hong Kong Law', l: 'Hong Kong Law'}, {v: 'Japanese Law', l: 'Japanese Law'},
                                {v: 'German Law', l: 'German Law'}, {v: 'French Law', l: 'French Law'}, {v: 'Swiss Law', l: 'Swiss Law'}
                              ]} 
                              onChange={(v: string) => handleFormDataChange({governingLaw: v})} 
                              labelClassName={labelClassName} 
                              inputClassName={inputClassName} 
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 bg-white sticky bottom-0">
                    {/* The server's reason when it gives one: "try again" is wrong advice for
                        a Counterparty ID Backoffice does not know, which fails the same way every time. */}
                    {activeMutation.isError && (
                      <p role="alert" className="mr-auto text-sm text-red-600">
                        {getErrorMessage(activeMutation.error, 'Failed to save. Please try again.')}
                      </p>
                    )}
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={!hasAnAgreement}
                      isLoading={activeMutation.isPending}
                    >
                      {isEditMode ? 'Save Changes' : 'Create Agreement'}
                    </Button>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>

    <Transition show={showCloseConfirmation} as={React.Fragment}>
      <Dialog as="div" className="relative z-[110]" onClose={() => setShowCloseConfirmation(false)}>
        <TransitionChild
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </TransitionChild>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                <div className="p-6">
                  <DialogTitle className="text-lg font-semibold text-gray-900 mb-2">
                    Discard Changes?
                  </DialogTitle>
                  <p className="text-sm text-gray-600 mb-6">
                    You have unsaved changes in the agreement form. Are you sure you want to close without saving?
                  </p>
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => setShowCloseConfirmation(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Continue Editing
                    </button>
                    <button
                      onClick={() => {
                        setShowCloseConfirmation(false);
                        handleCloseWithoutConfirmation();
                      }}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      Discard Changes
                    </button>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
    </>
  );
};

interface InputFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  disabled?: boolean;
  required?: boolean;
  labelClassName?: string;
  inputClassName?: string;
  placeholder?: string;
}

const InputField = ({ label, value, onChange, type = 'text', disabled = false, required = false, labelClassName, inputClassName, placeholder }: InputFieldProps) => {
  // Associated like SelectField's: a date input has no placeholder to be named by.
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className={labelClassName}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={clsx(inputClassName, disabled && 'bg-gray-50 text-gray-400')}
      />
    </div>
  );
};

interface SelectOption { v: string; l: string }
interface SelectFieldProps {
  label: string;
  value: string;
  options: SelectOption[];
  onChange: (v: string) => void;
  required?: boolean;
  labelClassName?: string;
  inputClassName?: string;
}

const SelectField = ({ label, value, options, onChange, required = false, labelClassName, inputClassName }: SelectFieldProps) => {
  // Associate the label with the control (it was decorative before): a select has no placeholder to
  // identify it by, so without this the field is unreachable to a screen reader and to a test.
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className={labelClassName}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={inputClassName}
      >
        {options.map((opt) => <option key={opt.v} value={opt.v}>{opt.l}</option>)}
      </select>
    </div>
  );
};
